import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const BannerAlertSchema = z.object({
  variant: z.enum(["tier-lock", "limit-reached", "upgrade-prompt"]),
  title: z.string(),
  message: z.string(),
  ctaLabel: z.string().optional(),
  ctaLink: z.string().optional(),
});

type BannerAlertData = z.infer<typeof BannerAlertSchema>;

/* ────────────────────── Variant Config ────────────────────── */

interface VariantConfig {
  wrapperClass: string;
  iconClass: string;
  titleClass: string;
  messageClass: string;
  buttonClass: string;
  icon: string; // SVG path d 값
}

const VARIANT_CONFIG: Record<BannerAlertData["variant"], VariantConfig> = {
  "tier-lock": {
    wrapperClass:
      "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40",
    iconClass: "text-amber-600 dark:text-amber-400",
    titleClass: "text-amber-900 dark:text-amber-200",
    messageClass: "text-amber-700 dark:text-amber-300",
    buttonClass:
      "bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600",
    // Lock icon path
    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  },
  "limit-reached": {
    wrapperClass:
      "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40",
    iconClass: "text-red-600 dark:text-red-400",
    titleClass: "text-red-900 dark:text-red-200",
    messageClass: "text-red-700 dark:text-red-300",
    buttonClass:
      "bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600",
    // AlertTriangle icon path
    icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  },
  "upgrade-prompt": {
    wrapperClass:
      "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/40",
    iconClass: "text-blue-600 dark:text-blue-400",
    titleClass: "text-blue-900 dark:text-blue-200",
    messageClass: "text-blue-700 dark:text-blue-300",
    buttonClass:
      "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
    // Sparkles icon path (simplified star-like shape)
    icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
  },
};

/* ────────────────────── Component ────────────────────── */

export function BannerAlert({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<BannerAlertData>) {
  const { variant, title, message, ctaLabel, ctaLink } = widget.data;
  const config = VARIANT_CONFIG[variant];

  function handleCta() {
    if (ctaLink && onDeepLink) {
      onDeepLink(ctaLink);
    } else if (widget.deepLink && onDeepLink) {
      onDeepLink(widget.deepLink);
    }
  }

  const showCta = !!(ctaLabel && (ctaLink || widget.deepLink) && onDeepLink);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-3",
        config.wrapperClass,
        className,
      )}
    >
      {/* 아이콘 */}
      <svg
        className={cn("size-5 shrink-0", config.iconClass)}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={config.icon} />
      </svg>

      {/* 텍스트 */}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-[13px] font-semibold leading-tight",
            config.titleClass,
          )}
        >
          {title}
        </p>
        <p
          className={cn("mt-0.5 text-[11px] leading-snug", config.messageClass)}
        >
          {message}
        </p>
      </div>

      {/* CTA 버튼 */}
      {showCta && (
        <button
          type="button"
          onClick={handleCta}
          className={cn(
            "shrink-0 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition-colors",
            config.buttonClass,
          )}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function BannerAlertSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border border-stroke-subtle px-4 py-3",
        className,
      )}
    >
      <Skeleton className="size-5 shrink-0 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="h-3 w-60" />
      </div>
      <Skeleton className="h-7 w-16 shrink-0 rounded-lg" />
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const bannerAlertWidget = defineWidget<BannerAlertData>({
  type: WIDGET_TYPES.BANNER_ALERT,
  label: "배너 알림",
  schema: BannerAlertSchema,
  component: BannerAlert,
  skeleton: BannerAlertSkeleton,
  defaultLayout: "full",
  maxHeight: 80,
  aiHint: "Tier 잠금 / 한도 도달 / 업그레이드 안내 배너",
});

registerWidget(bannerAlertWidget);
